import { Card, CardContent } from "@/components/ui/card";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { Button } from "@/components/ui/button";
import { MessageSquare, Play, Clock, Pause } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { usePodcastPlayer } from "@/context/PodcastPlayerContext";
import { T } from "@/i18n/T";

interface PodcastCommentActivityCardProps {
  comment: any;
}

const PodcastCommentActivityCard = ({ comment }: PodcastCommentActivityCardProps) => {
  const { currentEpisode, isPlaying, playEpisode, pause, resume } = usePodcastPlayer();
  const profile = comment.profiles || comment.user_profiles;
  const episode = comment.podcast_episodes;
  const podcast = episode?.podcast;

  const isCurrentEpisode = currentEpisode?.id === episode?.id;

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handlePlayClick = () => {
    if (!episode) return;
    
    if (isCurrentEpisode && isPlaying) {
      pause();
    } else if (isCurrentEpisode) {
      resume();
    } else {
      playEpisode({
        id: episode.id,
        podcast_id: episode.podcast_id,
        title: episode.title,
        description: episode.description || '',
        audio_url: episode.audio_url,
        image_url: episode.image_url,
        duration_seconds: episode.duration_seconds,
        published_at: episode.published_at,
        guid: episode.guid || '',
        is_published: true,
        likes_count: episode.likes_count || 0,
        comments_count: episode.comments_count || 0,
        created_at: episode.created_at,
        podcast: podcast
      });
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        {/* User Info */}
        <div className="flex items-start gap-3 mb-4">
          <ProfileAvatar
            userId={comment.user_id}
            avatarUrl={profile?.avatar_url}
            name={profile?.full_name || profile?.username}
            background={profile?.avatar_background}
            size="sm"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-foreground flex items-center gap-1">
                {profile?.full_name || profile?.username || 'Anonymous'}
              </span>
              <span className="text-muted-foreground"><T k="auto.commented-on-a-podcast-episode-29be" /></span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>

        {/* Comment Content */}
        <div className="mb-4 pl-12">
          <p className="text-foreground">{comment.content}</p>
        </div>

        {/* Episode Card */}
        {episode && (
          <div className="ml-12 border rounded-lg overflow-hidden hover:border-primary/50 transition-colors">
            <div className="flex gap-4 p-4">
              {/* Episode Image */}
              {episode.image_url && (
                <div className="relative w-24 h-24 flex-shrink-0 rounded-md overflow-hidden">
                  <img 
                    src={episode.image_url} 
                    alt={episode.title}
                    className="w-full h-full object-cover"
                  loading="lazy" decoding="async" />
                </div>
              )}

              {/* Episode Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-foreground line-clamp-2 mb-1">
                  {episode.title}
                </h4>
                {podcast && (
                  <p className="text-sm text-muted-foreground mb-2">
                    {podcast.name}
                  </p>
                )}
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  {episode.duration_seconds && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{formatDuration(episode.duration_seconds)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    <span>{episode.comments_count || 0}</span>
                  </div>
                </div>
              </div>

              {/* Single Play Button */}
              <div className="flex items-center">
                <Button
                  size="icon"
                  variant="outline"
                  className="rounded-full"
                  onClick={handlePlayClick}
                >
                  {isCurrentEpisode && isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4 ml-0.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PodcastCommentActivityCard;
